import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { tw, css } from 'twind/css';
import { Icon, IconProps } from '@global-ecom/nitro-uds/elements';

import Modal from 'ui/components/Modal';
import { useHorizontalScroll } from 'hooks/useHorizontalScroll';
import {
  ModalStateType,
  PixleeImgType,
  GalleryItemType,
} from 'ui/pages/ProductDetailsPages/types';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { ProductVideo, Video } from 'ui/components/Video';
import { ProductImage } from '__generated__/graphql';
import { ProductVideoMuteButton } from 'ui/pages/ProductDetailsPages/ProductVideoMuteButton';
import {
  AnalyticsEvents,
  event,
  transformImageUrlToImageInteractionAnalyticsEvent,
} from 'utils/analytics';
import { getIsMobile } from 'utils/media';
import { useTranslate } from 'hooks/useTranslations';

import CloseButton from './CloseButton';
import ChevronButton from './ChevronButton';

export const isVideo = (checkitem: GalleryItemType): boolean => {
  return checkitem?.__typename === 'ProductVideo';
};

const SocialImage = React.forwardRef<
  HTMLImageElement,
  {
    image: PixleeImgType;
    isSocialIconVisible: boolean;
  }
>(({ image, isSocialIconVisible }, ref) => {
  return (
    <>
      {isSocialIconVisible && image.source && (
        <div className="absolute z-10 top-3 right-3 text-puma-black-300">
          <Icon
            name={('social-' + image.source) as IconProps['name']}
            size="3xl"
          />
        </div>
      )}
      <img
        className="max-h-screen"
        key={image.href}
        src={image.href}
        alt={image.alt}
        ref={ref}
      />
    </>
  );
});

SocialImage.displayName = 'SocialImage';

const DisplayItem = React.forwardRef<
  any,
  {
    item: GalleryItemType;
    isSocialIconVisible: boolean;
    isGalleryVideoMuted: boolean;
  }
>(({ item, isSocialIconVisible, isGalleryVideoMuted }, ref) => {
  switch (item.__typename) {
    case 'ProductVideo':
      const video = item as ProductVideo;
      return (
        <Video
          src={video.src}
          videoOptions={video.videoOptions}
          muted={isGalleryVideoMuted}
          className="max-h-screen"
          ref={ref}
        />
      );
    case 'PixleeImg':
      const socialImage = item as PixleeImgType;
      return (
        <SocialImage
          image={socialImage}
          isSocialIconVisible={isSocialIconVisible}
          ref={ref}
        />
      );
    default:
      const image = item as ProductImage;
      return (
        <img
          className="max-h-screen"
          key={image.href}
          src={image.href}
          alt={image.alt}
          ref={ref}
        />
      );
  }
});

DisplayItem.displayName = 'DisplayItem';

const MAX_ZOOM = 2;

type ZoomImageProps = {
  image: GalleryItemType;
  pos: number;
  setPicTracker: Dispatch<SetStateAction<number>>;
  isSocialIconVisible: boolean;
  isVideoMuted: boolean;
};

const ZoomImage: React.FC<ZoomImageProps> = ({
  image,
  pos,
  setPicTracker,
  isSocialIconVisible,
  isVideoMuted,
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const ref = useRef<any>();
  let resetTransformCallback: (n?: number, t?: 'linear') => void;

  useIntersectionObserver(
    ref,
    () => setPicTracker(pos),
    true,
    {
      threshold: 1,
    },
    () => resetTransformCallback(100, 'linear')
  );

  const key = isVideo(image)
    ? (image as ProductVideo).src
    : (image as ProductImage | PixleeImgType).href;

  return (
    <li
      key={key}
      data-test-id="gallery-image"
      className={tw(
        `flex items-center justify-center w-full h-full flex-none`,
        css({ scrollSnapAlign: 'start' })
      )}
    >
      <TransformWrapper
        doubleClick={{ disabled: true }}
        centerOnInit={true}
        panning={{
          disabled: getIsMobile() || !isZoomed,
          lockAxisY: true,
        }}
        wheel={{ disabled: getIsMobile(), step: 0.06 }}
        maxScale={2.5}
      >
        {({ setTransform, resetTransform, state }) => {
          resetTransformCallback = resetTransform;
          return (
            <div
              onClick={event => {
                if (state.scale < MAX_ZOOM) {
                  setTransform(
                    Number(
                      (
                        state.positionX -
                        event.nativeEvent.clientX * 0.75
                      ).toFixed(2)
                    ),
                    Number(
                      (
                        state.positionY -
                        event.nativeEvent.clientY * 0.75
                      ).toFixed(2)
                    ),
                    state.scale + 0.75
                  );
                } else {
                  resetTransform();
                }
                setIsZoomed(state.scale < MAX_ZOOM);
              }}
            >
              <TransformComponent
                wrapperStyle={{
                  cursor: state.scale > MAX_ZOOM ? 'zoom-out' : 'zoom-in',
                  overflow: state.scale > 1 ? 'visible' : 'hidden',
                }}
              >
                <DisplayItem
                  item={image}
                  isGalleryVideoMuted={isVideoMuted}
                  isSocialIconVisible={isSocialIconVisible}
                  ref={ref}
                />
              </TransformComponent>
            </div>
          );
        }}
      </TransformWrapper>
    </li>
  );
};

export type CarouselModalProps = {
  modalState: ModalStateType;
  onClose: () => void;
  pixleeImages?: PixleeImgType[];
  isSocialIconVisible: boolean;
  galleryItems?: GalleryItemType[];
  triggerEvent?: () => void;
};

export const CarouselModal: React.FC<CarouselModalProps> = ({
  pixleeImages = [],
  modalState,
  onClose,
  isSocialIconVisible,
  galleryItems = [],
  triggerEvent,
}) => {
  const t = useTranslate();

  const images = useMemo(
    () =>
      galleryItems.concat(pixleeImages).map((image, i) => ({
        ...image,
        pos: i + 1,
      })),
    [pixleeImages, galleryItems]
  );
  const carouselImages =
    images.length > 1
      ? [images[images.length - 1], ...images, images[0]]
      : images;

  const mainImageContainer = useHorizontalScroll({ infinite: true });
  const [picTracker, setPicTracker] = useState(modalState.picIndex + 1);
  const [isGalleryVideoMuted, setIsGalleryVideoMuted] = useState(
    modalState?.muted ?? true
  );

  useEffect(() => {
    setTimeout(() =>
      mainImageContainer.scrollToSelected(modalState.picIndex + 1)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const scroller = mainImageContainer.ref.current;
    const updateScroll = () => {
      if (scroller.scrollLeft === 0 && picTracker === images.length) {
        mainImageContainer.scrollToSelected(picTracker);
      } else if (
        scroller.scrollLeft + scroller.clientWidth === scroller.scrollWidth &&
        picTracker === 1
      ) {
        mainImageContainer.scrollToSelected(picTracker);
      }
    };
    updateScroll();
    document.addEventListener('scroll', updateScroll);
    return () => document.removeEventListener('scroll', updateScroll);
  }, [picTracker, images.length, mainImageContainer]);

  useEffect(() => {
    const img = images[picTracker - 1];
    // This is a PISA product image not a video or pixlee image
    if (img.__typename !== 'ProductImage') return;
    // We are not on the image that the gallery was initially loaded with
    if (picTracker - 1 !== modalState.picIndex) {
      event(AnalyticsEvents.PDP_INTERACTION, {
        interactionType: 'Gallery View',
        ...transformImageUrlToImageInteractionAnalyticsEvent(
          (img as ProductImage).href
        ),
      });
    }
    if (triggerEvent) {
      triggerEvent();
    }
  }, [picTracker]); // eslint-disable-line react-hooks/exhaustive-deps

  const muteVideoOnChangeSlide = useCallback(() => {
    if (isVideo(carouselImages[picTracker])) {
      setIsGalleryVideoMuted(true);
    }
  }, [picTracker]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal layout="fullscreen">
      <CloseButton className="absolute right-5 top-5 z-50" onClick={onClose} />
      <div
        className={`order-1 relative group-images h-[${window?.innerHeight}px]`}
        data-test-id="gallery-modal-zoom"
      >
        <div
          tabIndex={0}
          role="group"
          aria-label="Gallery"
          className="absolute inset-0"
        >
          <ul
            ref={mainImageContainer.ref}
            data-test-id="gallery-images"
            className={tw(
              'flex w-full h-full relative overflow-x-scroll no-scrollbars!',
              css({ scrollSnapType: 'x mandatory' })
            )}
          >
            {carouselImages.map((image, i) => (
              <ZoomImage
                key={i + 1}
                image={image}
                isVideoMuted={isGalleryVideoMuted}
                pos={image.pos}
                setPicTracker={setPicTracker}
                isSocialIconVisible={isSocialIconVisible}
              />
            ))}
          </ul>
        </div>
        <ChevronButton
          className="absolute left-2 top-1/2 transform -translate-y-1/2"
          label={t('scrollLeft')}
          onClick={() => {
            muteVideoOnChangeSlide();
            mainImageContainer.prev();
          }}
          disabled={!mainImageContainer.hasPrev}
          direction="left"
        />
        <ChevronButton
          className="absolute right-2 top-1/2 transform -translate-y-1/2"
          label={t('scrollRight')}
          disabled={!mainImageContainer.hasNext}
          direction="right"
          onClick={() => {
            muteVideoOnChangeSlide();
            mainImageContainer.next();
          }}
        />

        {isVideo(carouselImages[picTracker]) && (
          <ProductVideoMuteButton
            isMuted={isGalleryVideoMuted}
            setIsMuted={setIsGalleryVideoMuted}
            className={tw(
              'bg-white text-black rounded-full border-transparent absolute bottom-10 right-3.5 z-10 m-0 p-2'
            )}
          />
        )}
      </div>
      <div
        className="absolute bottom-12 left-12"
        data-test-id="image-gallery-tracker-position"
      >
        {picTracker.toString().padStart(2, '0')}
        <span className="inline-block bg-black h-px my-1 mx-2.5 w-7"></span>
        {images.length.toString().padStart(2, '0')}
      </div>
    </Modal>
  );
};
